import type { UserClaim, UserPolicy } from '@/types';
import { segmentTrack } from '@/utils/analytics';

const ActionButtonIds = [
  'nav-policies',
  'nav-claims',
  'contact-us',
  'view-claims',
  'view-policies',
  'important-information',
  'claim-upload-document',
  'policy-make-claim',
  'policy-manage',
  'claim-resume',
  'policy-pds',
  'policy-coi',
] as const;

type ActionButtonId = (typeof ActionButtonIds)[number];

type ActionProperties = Partial<Pick<UserPolicy, 'policyNumber'> & Pick<UserClaim, 'claimNo'>>;

const EMPTY_POLICY_NO = '000000000000';

const EMPTY_CLAIM_NO = '0000000000';

function isIdOfTypeActionButtonId(id: string | number): id is ActionButtonId {
  return (ActionButtonIds as readonly string[]).includes(`${id}`);
}

const segmentTrackActionButtonClick = (id: string | number, properties?: ActionProperties) => {
  if (!isIdOfTypeActionButtonId(id)) {
    return;
  }

  const { policyNumber = EMPTY_POLICY_NO } = properties ?? {};
  const claimNo = `${properties?.claimNo ?? EMPTY_CLAIM_NO}`;

  switch (id) {
    case 'nav-policies': {
      return segmentTrack('Policies Header Menu Clicked', undefined);
    }

    case 'nav-claims': {
      return segmentTrack('Claims Header Menu Clicked', undefined);
    }

    case 'contact-us': {
      return segmentTrack('Contact Us Link Clicked', undefined);
    }

    case 'view-claims': {
      return segmentTrack('View Your Claims Card Clicked', undefined);
    }

    case 'view-policies': {
      return segmentTrack('View Your Policies Card Clicked', undefined);
    }

    case 'important-information': {
      return segmentTrack('Important Information Card Clicked', undefined);
    }

    case 'claim-upload-document': {
      return segmentTrack('Upload Documents Button Clicked', { policyNumber, claimNo });
    }

    case 'claim-resume': {
      return segmentTrack('Resume Claim Button Clicked', { policyNumber, claimNo });
    }

    case 'policy-manage': {
      return segmentTrack('Manage My Policy Button Clicked', { policyNumber });
    }

    case 'policy-make-claim': {
      return segmentTrack('Make A Claim Button Clicked', { policyNumber });
    }

    case 'policy-pds': {
      return segmentTrack('View PDS Link Clicked', { policyNumber });
    }

    case 'policy-coi': {
      return segmentTrack('Certificate Of Insurance Link Clicked', {
        policyNumber,
      });
    }

    default: {
      return;
    }
  }
};

export { segmentTrackActionButtonClick };
